/**
 * TrackResultPanel — the result published for a track (the Published Track
 * Result, glossary) and the ball holder's act of publishing one.
 *
 * Owner ruling 2026-10-15 (migration 934): closing a session publishes nothing
 * for the track. The result goes back into Catchball, where the person who
 * holds the ball decides whether to publish it for the track. This panel reads
 * the newest `track_results` row and, for a person who may publish, offers the
 * act: one session, or — under the "combined" rule — the comparable closed
 * sessions averaged by rank. The control is not drawn for anyone else
 * (build contract §2.8).
 *
 * Shown on the track page's Results tab and on /prioritization/tracks/:id/result.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel, DataTable, Callout, EmptyState, type Column } from '@/components/ds';
import { ObjectRef } from '@/components/ds/ObjectRef';
import { RankMark } from '@/components/prioritization/RankMark';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTrackSessions } from '@/stores/prioritization/sessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  usePublishedTrackResult,
  useCurrentTrackResult,
  useTrackResultCandidates,
  publishTrackResult,
  type TrackResultItem,
  type TrackResultCandidate,
} from '@/lib/tracks/publishedTrackResult';
import { formatDate } from '@/i18n/formatters';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { itemInContextHref } from '@/lib/itemCycleStanding';

/** Month, day and year — the one date form this page shows. */
const TRACK_RESULT_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

const BUTTON =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--text-pri)] text-[var(--bg-raised)] hover:opacity-90 disabled:opacity-50';
const QUIET_BUTTON =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-pri)] hover:bg-[var(--hover-tint)] disabled:opacity-50';

export function TrackResultPanel({
  trackId,
  refreshKey,
  onPublished,
  showPageLink = false,
}: {
  trackId: string;
  /** Change it to refetch (a session closed, the ball moved). */
  refreshKey?: unknown;
  onPublished?: () => void;
  /** Link to the result's own page (from the track page's tab). */
  showPageLink?: boolean;
}) {
  const cycleId = useTrackStore((s) => s.tracks.find((t) => t.id === trackId)?.cycleId);
  const [tick, setTick] = useState(0);
  const key = `${String(refreshKey ?? '')}:${tick}`;
  const { result: summary } = usePublishedTrackResult(trackId, key);
  const { record, loading } = useCurrentTrackResult(trackId, key);
  const { data: desk } = useTrackResultCandidates(summary?.mayPublish ? trackId : null, key);
  const items = useItemsStore((s) => s.items);
  const rawSessions = useTrackSessions(trackId);
  const sessions = Array.isArray(rawSessions) ? rawSessions : [];
  const userName = useUserStore((s) => s.userName) ?? '';

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'green' | 'coral'; text: string } | null>(null);

  const titleOf = (id: string) => items.find((i) => i.id === id)?.title ?? 'Unknown item';
  const sessionNameOf = (id: string) =>
    sessions.find((s) => s.id === id)?.name ??
    desk?.candidates.find((c) => c.sessionId === id)?.sessionName ??
    (summary?.sessionId === id ? summary.sessionName : null) ??
    'Session';

  const combined = record?.strategy === 'aggregate';
  const sourceIds = record?.sourceSessionIds ?? [];

  const columns = useMemo<Column<TrackResultItem>[]>(() => {
    const cols: Column<TrackResultItem>[] = [
      {
        key: 'rank',
        header: 'Rank',
        width: '4.5rem',
        render: (r) => (
          <RankMark
            rank={r.rank}
            official={false}
            aboveTheLine={record?.cutLinePosition != null ? r.rank <= record.cutLinePosition : null}
            size="sm"
          />
        ),
      },
      {
        key: 'item',
        header: 'Item',
        render: (r) => (
          <Link to={itemInContextHref(r.itemId, { cycleId })} className="text-[var(--text-pri)] hover:underline">
            {titleOf(r.itemId)}
          </Link>
        ),
      },
    ];
    if (combined) {
      cols.push({
        key: 'avg',
        header: 'Average rank',
        numeric: true,
        width: '8rem',
        render: (r) => (r.averageRank != null ? r.averageRank.toFixed(2) : ''),
      });
      sourceIds.forEach((sid, i) => {
        cols.push({
          key: `src-${i}`,
          header: sessionNameOf(sid),
          numeric: true,
          render: (r) => (r.sessionRanks[sid] != null ? String(r.sessionRanks[sid]) : ''),
        });
      });
    } else if (record?.items.some((i) => i.score != null)) {
      cols.push({
        key: 'score',
        header: 'Score',
        numeric: true,
        width: '6rem',
        render: (r) => (r.score != null ? r.score.toFixed(1) : ''),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, combined, sourceIds.join(','), items, sessions, desk]);

  const publish = async (sessionId?: string) => {
    setBusy(sessionId ?? 'combine');
    setMessage(null);
    const out = await publishTrackResult({ trackId, sessionId, actorName: userName });
    setBusy(null);
    if (!out.ok) {
      setMessage({ tone: 'coral', text: out.error ?? 'The server refused to publish this result.' });
      return;
    }
    const n = out.sourceSessionIds?.length ?? 1;
    setMessage({
      tone: 'green',
      text:
        out.strategy === 'aggregate'
          ? `Published for the track: ${n} session${n === 1 ? '' : 's'} combined, ${out.itemCount ?? 0} items.`
          : `Published for the track: ${out.itemCount ?? 0} items.`,
    });
    setTick((t) => t + 1);
    onPublished?.();
  };

  // ── The ball holder's act ──────────────────────────────────────────────────
  const mayPublish = Boolean(desk?.mayPublish);
  const pending: TrackResultCandidate[] = (desk?.candidates ?? []).filter((c) => !c.inPublishedResult);
  const comparablePending = pending.filter((c) => c.comparable);
  const isAggregateRule = desk?.strategy === 'aggregate';

  const act =
    mayPublish && pending.length > 0 ? (
      <Callout
        tone="mustard"
        label="The ball is with you"
        title={
          record
            ? `${pending.length} session${pending.length === 1 ? ' has' : 's have'} closed since this result was published.`
            : 'Nothing has been published for this track yet.'
        }
        className="mb-4"
      >
        <p className="text-sm text-[var(--text-sec)]">
          What you publish here feeds the Plan of Record and what goes up through Catchball.
        </p>
        {isAggregateRule ? (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-[var(--text-pri)]">
              This track combines its sessions by average rank.{' '}
              {comparablePending.length > 0
                ? `${(desk?.candidates ?? []).filter((c) => c.comparable).length} closed session${(desk?.candidates ?? []).filter((c) => c.comparable).length === 1 ? '' : 's'} ranked the same items and would be combined.`
                : 'None of the newer sessions ranked the same items as the newest, so only the newest would count.'}
            </p>
            <ul className="text-xs text-[var(--text-ter)] space-y-0.5">
              {(desk?.candidates ?? []).map((c) => (
                <li key={c.sessionId}>
                  {c.sessionName ?? 'Untitled session'} · closed {formatDate(c.closedAt ?? '', TRACK_RESULT_DATE)} · {c.itemCount} items
                  {c.comparable ? '' : ' · left out: ranked different items'}
                </li>
              ))}
            </ul>
            <button type="button" className={BUTTON} disabled={busy !== null} onClick={() => void publish()}>
              {busy === 'combine' ? 'Publishing…' : 'Combine and publish for the track'}
            </button>
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-[var(--rule)]">
            {pending.map((c) => (
              <li key={c.sessionId} className="py-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-[var(--text-pri)]">
                  <ObjectRef kind="session" id={c.sessionId} name={c.sessionName ?? 'Untitled session'} hideWord />
                  <span className="text-xs text-[var(--text-ter)] ml-2">
                    closed {formatDate(c.closedAt ?? '', TRACK_RESULT_DATE)} · {c.itemCount} items
                  </span>
                </span>
                <button
                  type="button"
                  className={record ? QUIET_BUTTON : BUTTON}
                  disabled={busy !== null}
                  onClick={() => void publish(c.sessionId)}
                >
                  {busy === c.sessionId ? 'Publishing…' : 'Publish for the track'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Callout>
    ) : null;

  const header = (
    <Panel
      label="Track result"
      title="Published result"
      actions={
        showPageLink ? (
          <Link to={`/prioritization/tracks/${trackId}/result`} className="text-xs underline text-[var(--text-ter)] hover:text-[var(--text-pri)]">
            Open the result
          </Link>
        ) : undefined
      }
      flush={Boolean(record)}
      bodyClassName={record ? '' : undefined}
    >
      {message && (
        <div className={`px-4 pt-3 text-sm ${message.tone === 'green' ? 'text-[var(--green)]' : 'text-[var(--coral)]'}`} data-testid="track-result-message">
          {message.text}
        </div>
      )}
      {act && <div className="px-4 pt-4">{act}</div>}

      {loading && !record ? (
        <p className="px-4 py-3 text-sm text-[var(--text-ter)]">Reading the track's result…</p>
      ) : !record ? (
        <EmptyState
          size="inline"
          title="No result has been published for this track yet"
          description={
            summary
              ? `${summary.reason.charAt(0).toUpperCase()}${summary.reason.slice(1)}. The person who holds the ball decides what is published for the track.`
              : 'The person who holds the ball decides what is published for the track.'
          }
          action={
            <Link to={`/prioritization/tracks/${trackId}?tab=sessions`} className="text-sm underline text-[var(--text-pri)]">
              See the track's sessions
            </Link>
          }
        />
      ) : (
        <>
          <div className="px-4 py-3 text-sm text-[var(--text-sec)] border-b border-[var(--rule)]" data-testid="track-published-result">
            {combined ? (
              <>
                Combined from{' '}
                {sourceIds.map((sid, i) => (
                  <span key={sid}>
                    {i > 0 ? ', ' : ''}
                    <ObjectRef kind="session" id={sid} name={sessionNameOf(sid)} hideWord />
                  </span>
                ))}{' '}
                by average rank
                {record.leftOut.length > 0 && (
                  <>
                    ; left out: {record.leftOut.map((l) => `${l.sessionName ?? 'a session'} (${l.reason})`).join(', ')}
                  </>
                )}
                .
              </>
            ) : (
              <>
                From <ObjectRef kind="session" id={sourceIds[0]} name={sessionNameOf(sourceIds[0] ?? '')} hideWord />.
              </>
            )}{' '}
            Published for the track by {record.publishedByName ?? 'the person who held the ball'} on {formatDate(record.publishedAt ?? '', TRACK_RESULT_DATE)}.
            {record.cutLinePosition != null && <> Cut line after {record.cutLinePosition}.</>}
            {record.supersedesId && <> Replaces an earlier result.</>}
            {record.note && <> Note: {record.note}</>}
          </div>
          <DataTable<TrackResultItem>
            columns={columns}
            rows={record.items}
            getRowKey={(r) => r.itemId}
            density="compact"
            bordered={false}
            getRowAccent={(r) =>
              record.cutLinePosition != null && r.rank <= record.cutLinePosition ? 'green' : undefined
            }
          />
        </>
      )}
    </Panel>
  );

  return header;
}

export default TrackResultPanel;
